import React, { useEffect, useState } from "react";
import ConfiguredVitalTable from "./configuredVitalTable";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Flex, Modal, Spin } from "antd";
import nodatafound from "../../../../../Assets/Icons/NoDataCard2.svg";
import {
  fetchRpmConfiguredVitalsList,
  setSelectedRpmPatient,
} from "../../../../../Redux/actions/rpmActions";
import { CiCirclePlus } from "react-icons/ci";
import VitalService from "../../../../../Services/VitalService";
import { filterCurrentMonthVitals } from "../../../../../utils/dates";
import AddVitals from "./addConfigVitals";
import VitalTrends from "./vitalTrends";
import dayjs from "dayjs";
import WatchData from "./watchData";

const vitalStyleArray = [
  {
    title: "Blood Pressure",
    color: "#FB7800",
    backgroundColor: "#FFD5B8",
    icon: require("../../../../../Assets/Icons/Vital/Blood-Pressure.svg")
      .default,
    vitalCode: "BP",
    unit: "mmHg",
  },
  {
    title: "BMI",
    color: "#14BC57",
    backgroundColor: "#B5FFD2",
    icon: require("../../../../../Assets/Icons/Vital/Weight.svg").default,
    vitalCode: "BMI",
    unit: "Kg/m²",
  },
  {
    title: "Blood Glucose",
    color: "#9421F3",
    backgroundColor: "#E0C1FF",
    icon: require("../../../../../Assets/Icons/Vital/Blood-Glucose.svg")
      .default,
    vitalCode: "BG",
  },
  {
    title: "Heart Rate",
    color: "#FC1D52",
    backgroundColor: "#FFCDD8",
    icon: require("../../../../../Assets/Icons/Vital/Heart-Rate.svg").default,
    vitalCode: "HR",
    unit: "bpm",
  },
  {
    title: "Respiratory Rate",
    color: "#FFB800",
    backgroundColor: "#FCFEA5",
    icon: require("../../../../../Assets/Icons/Vital/respiration.svg").default,
    vitalCode: "RR",
    unit: "bpm",
  },
  {
    title: "SpO2",
    color: "#6500F0",
    backgroundColor: "#E9DCFB",
    icon: require("../../../../../Assets/Icons/Vital/Oxygen.svg").default,
    vitalCode: "PO",
    unit: "%",
  },
  {
    title: "Temperature",
    color: "#4880DE",
    backgroundColor: "#B8DAFF",
    icon: require("../../../../../Assets/Icons/Vital/Temperature.svg").default,
    vitalCode: "TP",
    unit: "°F",
  },
  {
    title: "Weight",
    color: "#964B00",
    backgroundColor: "#EFD8C5",
    icon: require("../../../../../Assets/newicons/pulse.svg").default,
    vitalCode: "WE",
    unit: "Kg",
  },
  {
    title: "Height",
    color: "#008080",
    backgroundColor: "#C1EFEF",
    icon: require("../../../../../Assets/newicons/temp.svg").default,
    vitalCode: "HI",
    unit: "cm",
  },
];

function Index() {
  const [currentPage, setCurrentPage] = useState(1);
  const [savedVitalsList, setSavedVitalsList] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [latestVitalData, setLatestVitalData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const { isLoading, rpmConfigVitalsList, selectedRpmPatient } = useSelector(
    (state) => state.rpm
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedRpmPatient) {
      const rpmPatientJson = localStorage.getItem("rpmPatient");
      const rpmPatientObject = rpmPatientJson
        ? JSON.parse(rpmPatientJson)
        : null;
      if (rpmPatientObject) dispatch(setSelectedRpmPatient(rpmPatientObject));
    }
  }, []);

  useEffect(() => {
    const payload = {
      patientId: selectedRpmPatient?.patientId,
      status: 1,
    };
    if (selectedRpmPatient?.patientId) {
      dispatch(fetchRpmConfiguredVitalsList(payload));
      fetchPatientVitals();
    }
  }, [selectedRpmPatient?.patientId]);

  const fetchPatientVitals = async (selectedMonth = dayjs()) => {
    const payload = {
      account_Id: selectedRpmPatient?.account_Id,
      patientId: selectedRpmPatient?.patientId,
      status: 1,
    };

    try {
      const response = await VitalService.getVitalsData(payload);
      const currMonthTimeRecord = filterCurrentMonthVitals(
        response.data,
        selectedMonth
      );
      const finalVitalArray = [];

      for (let item of currMonthTimeRecord) {
        const detailPayload = {
          account_Id: selectedRpmPatient?.account_Id,
          patientId: selectedRpmPatient?.patientId,
          fromDate: item.recordedDateTime,
          toDate: item.recordedDateTime,
          status: 1,
        };

        const vitalObjectResponse = await VitalService.getSpecificVitalData(
          detailPayload
        );
        const vitalSet = Array.isArray(vitalObjectResponse.data)
          ? vitalObjectResponse.data
          : [];

        const vitalRow = {
          recordedDateTime: item.recordedDateTime,
          BMI: null,
          HR: null,
          BPS: null,
          BPD: null,
          WE: null,
          HI: null,
          PO: null,
          TP: null,
          RR: null,
          BG: null,
        };

        for (let v of vitalSet) {
          switch (v.vitalCode) {
            case "BMI":
              vitalRow.BMI = v.value;
              break;
            case "HR":
              vitalRow.HR = v.value;
              break;
            case "BPS":
              vitalRow.BPS = v.value;
              break;
            case "BPD":
              vitalRow.BPD = v.value;
              break;
            case "WE":
              vitalRow.WE = v.value;
              break;
            case "HI":
              vitalRow.HI = v.value;
              break;
            case "PO":
              vitalRow.PO = v.value;
              break;
            case "TP":
              vitalRow.TP = v.value;
              break;
            case "RR":
              vitalRow.RR = v.value;
              break;
            case "BG":
              vitalRow.BG = v.value;
              break;
            default:
              break;
          }
        }

        finalVitalArray.push(vitalRow);
      }

      setSavedVitalsList(finalVitalArray);
      setLatestVitalData(finalVitalArray[0]);
    } catch (e) {
      console.error(e);
    }
  };

  const vitalDisplayMap = {
    systolic: "BPS",
    diastolic: "BPD",
    height: "HI",
    weight: "WE",
    bmi: "BMI",
    bloodGlucose: "BG",
    temperature: "TP",
    spo2: "PO",
    heartRate: "HR",
    respiratoryRate: "RR",
  };

  const handleClosemodal = () => {
    setIsModalVisible(false);
    fetchPatientVitals();
  };

  const handleNewVitalEntry = () => {
    setIsModalVisible(true);
  };

  const getVitalValue = (key) => {
    switch (key) {
      case "heartRate":
        return latestVitalData?.["HR"];
      case "temperature":
        return latestVitalData?.["TP"];
      case "systolic":
      case "diastolic":
        const systolic = latestVitalData?.["BPS"];
        const diastolic = latestVitalData?.["BPD"];
        return systolic && diastolic
          ? `${systolic}/${diastolic}`
          : systolic || diastolic || "--";
      case "bmi":
        return latestVitalData?.["BMI"];
      case "height":
        return latestVitalData?.["HI"];
      case "weight":
        return latestVitalData?.["WE"];
      case "spo2":
        return latestVitalData?.["PO"];
      case "respiratoryRate":
        return latestVitalData?.["RR"];
      case "bloodGlucose":
        return latestVitalData?.["BG"];
      default:
        return "--";
    }
  };

  return (
    <div className="mt-3">
      {/* Configured vital cards */}
      <div
        className="grid-cols gap-6 overflow-x-auto w-full overflow-y-clip scrollWidth"
        style={{ display: "-webkit-inline-box" }}
        id="unquie-scroll"
      >
        {(() => {
          const filteredVitals = Object.entries(
            rpmConfigVitalsList || {}
          ).filter(([key, value]) => value === 1 && vitalDisplayMap[key]);

          const hasBP = filteredVitals.some(
            ([key]) => key === "systolic" || key === "diastolic"
          );
          const uniqueKeys = new Set(filteredVitals.map(([key]) => key));

          if (hasBP) {
            uniqueKeys.delete("systolic");
            uniqueKeys.delete("diastolic");
            uniqueKeys.add("bp");
          }

          if (uniqueKeys.size === 0) {
            return (
              <div className="flex flex-col items-center justify-center w-full">
                <p className="font-medium text-xl">No data found!!</p>
              </div>
            );
          }

          return Array.from(uniqueKeys).map((key) => {
            let vitalCode, vitalData, vitalValue;

            if (key === "bp") {
              vitalCode = "BP";
              vitalData = vitalStyleArray.find(
                (item) => item.vitalCode === vitalCode
              );
              vitalValue = getVitalValue("systolic");
            } else {
              vitalCode = vitalDisplayMap[key];
              vitalData = vitalStyleArray.find(
                (item) => item.vitalCode === vitalCode
              );
              vitalValue = getVitalValue(key);
            }

            return (
              <div
                key={vitalCode + key}
                className="rounded-lg p-3 h-auto border-b-4"
                style={{
                  backgroundColor: vitalData?.backgroundColor,
                  width: "300px",
                  borderColor: vitalData?.color,
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-gray-700 mb-3 text-base font-medium text-left">
                      {vitalData?.title}
                    </div>
                    <div className="text-gray-800 mb-3 text-2xl font-semibold text-left">
                      {vitalValue} {vitalData?.unit}
                    </div>
                  </div>
                  <img
                    src={vitalData?.icon}
                    alt={vitalData?.title}
                    width={35}
                    className="mr-3"
                  />
                </div>
              </div>
            );
          });
        })()}
      </div>

      <WatchData selectedRpmPatient={selectedRpmPatient} />

      {/* New and Filter */}
      <div className="flex justify-between items-center mt-5">
        <h1 className="font-bold text-xl">Vitals</h1>
        <div className="flex gap-4">
          <button
            onClick={handleNewVitalEntry}
            className="w-20 h-8 px-2 flex bg-[#F1FFF9] items-center gap-2 border border-[#3B98B9] rounded-md"
          >
            <CiCirclePlus className="w-5 h-5 text-[#189FAA]" />
            <span>New</span>
          </button>
          {/* <button className="w-20 h-9 px-2 flex items-center rounded-md gap-2 bg-brand-primaryColor">
                        <img src={FilterIcon} alt="Icon" />
                        <span className="text-white">Filter</span>
                    </button> */}
          <DatePicker
            picker="month"
            defaultValue={dayjs()}
            allowClear={false}
            onChange={(date) => fetchPatientVitals(date)}
          />
        </div>
      </div>

      {/* Vital Table */}
      <div
        className={`${
          isLoading === false && savedVitalsList?.length === 0
            ? "w-full flex justify-center items-center"
            : "overflow-y-scroll h-[23rem] scrollWidth mt-5"
        }`}
      >
        {isLoading && savedVitalsList?.length === 0 ? (
          <div className="flex items-center justify-center mt-[10%]">
            <Flex align="center" gap="middle">
              <Spin size="large" tip="Loading" />
            </Flex>
          </div>
        ) : savedVitalsList?.length === 0 ? (
          <div className="flex flex-col items-center justify-center">
            <img src={nodatafound} alt="nodatafound" />
            <p className="font-medium text-xl">No data found!!</p>
          </div>
        ) : (
          <ConfiguredVitalTable
            data={savedVitalsList}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalCount={savedVitalsList?.length}
            rpmConfigVitalsList={rpmConfigVitalsList}
            vitalDisplayMap={vitalDisplayMap}
          />
        )}
      </div>
      <hr className="mt-3" />
      <div>
        {savedVitalsList && (
          <VitalTrends
            data={savedVitalsList}
            rpmConfigVitalsList={rpmConfigVitalsList}
            vitalDisplayMap={vitalDisplayMap}
          />
        )}
      </div>
      <Modal
        title={<span className="font-bold text-base">Add Vitals</span>}
        open={isModalVisible}
        footer={[]}
        onCancel={handleClosemodal}
        width={800}
        destroyOnClose
      >
        <AddVitals
          onClose={handleClosemodal}
          handleClosemodal={handleClosemodal}
          selectedPatient={selectedRpmPatient}
          rpmConfigVitalsList={rpmConfigVitalsList}
          vitalDisplayMap={vitalDisplayMap}
        />
      </Modal>
    </div>
  );
}

export default Index;
